import React, { useEffect, useMemo, useState } from 'react';

const CHAR_DELAY_MS = 16;
const LINE_PAUSE_MS = 260;

/**
 * A computer-terminal readout that types itself out line by line, like a live transmission
 * being received rather than static text - click/tap anywhere on it to skip straight to the
 * full message, same "don't force her to wait" courtesy SubtitleDisplay's skip already gives
 * dialogue elsewhere in this room.
 */
function TerminalTypewriter({ lines }) {
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);

  useEffect(() => {
    setLineIndex(0);
    setCharIndex(0);
  }, [lines]);

  useEffect(() => {
    if (lineIndex >= lines.length) return undefined;
    const currentLine = lines[lineIndex];
    if (charIndex >= currentLine.length) {
      const timer = setTimeout(() => {
        setLineIndex((i) => i + 1);
        setCharIndex(0);
      }, LINE_PAUSE_MS);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => setCharIndex((c) => c + 1), CHAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [lineIndex, charIndex, lines]);

  const handleSkip = () => {
    setLineIndex(lines.length);
    setCharIndex(0);
  };

  return (
    <div
      className="acb-briefing-terminal acb-mono"
      onClick={handleSkip}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSkip()}
      aria-label="Transmission - click to skip ahead"
    >
      {lines.slice(0, Math.min(lineIndex, lines.length)).map((line, i) => (
        <div key={i} className="acb-briefing-terminal-line">{line}</div>
      ))}
      <div className="acb-briefing-terminal-line">
        {lineIndex < lines.length ? lines[lineIndex].slice(0, charIndex) : ''}
        <span className="acb-briefing-cursor">&#9608;</span>
      </div>
    </div>
  );
}

/**
 * Shown once at the start of each mission, before she gets control of anything - same role as
 * Governor's Console's welcome modal (a pause-and-brief screen on entry), styled to this room's
 * own dark rover-console theme instead of that room's parchment one, typed out on a terminal
 * readout like a live incoming transmission rather than static text. Exact three-part wording
 * per the room's own spec: an opening line, the mission goal (puzzle.clue), a closing line -
 * no header block, no directives list, on purpose - the step-by-step breakdown already lives
 * in the in-puzzle Mission Notes, this is just the goal.
 */
function MissionBriefing({ icon, puzzle, onBegin }) {
  const lines = useMemo(() => {
    const result = ['Incoming Transmission ...', ''];
    if (puzzle.clue) result.push(`Mission goal : ${puzzle.clue}`, '');
    result.push('Transmission ends.');
    return result;
  }, [puzzle]);

  return (
    <div className="acb-briefing-scrim">
      <div className="acb-briefing-modal">
        <div className="acb-briefing-emoji">{icon}</div>
        <h2 className="acb-briefing-title">{puzzle.title}</h2>
        <TerminalTypewriter lines={lines} />
        <button type="button" className="acb-btn acb-briefing-btn" onClick={onBegin}>Acknowledge &amp; Begin</button>
      </div>
    </div>
  );
}

export default MissionBriefing;
